import React, { ReactElement, useState, useCallback } from 'react'
import {
    LineChart, Line,
    XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer
} from 'recharts'
import { GraphData, GraphDataRow, GraphAxisGroup, TickFormatter, OutlierDay, OutlierMarker,
    GRAPH_TARGET_TICKS, GRAPH_MARGIN, GRAPH_COLOURS,
    GraphSpanDelimitersProps,
    GraphLegendProps, GraphLegendItem,
    GraphProps } from './Graph.d'
import { formatDate } from '../../helpers/format'

export const snapToNearest = (target: string, dates: string[]): string =>
    dates.reduce((best, d) =>
        Math.abs(new Date(d).getTime() - new Date(target).getTime())
        < Math.abs(new Date(best).getTime() - new Date(target).getTime())
            ? d
            : best)

export const formatDateTick: TickFormatter = (value) => {
    const d = new Date(String(value))
    return isNaN(d.getTime()) ? String(value) : formatDate(d, 'long')
}

export const xAxisInterval = (chartRows: GraphDataRow[]): number =>
    chartRows.length > GRAPH_TARGET_TICKS
        ? Math.floor(chartRows.length / GRAPH_TARGET_TICKS)
        : 0

export const calculateDuration = (chartRows: GraphDataRow[], xAxis: string): number => {
    const first = String(chartRows[0]?.[xAxis] ?? '')
    const last = String(chartRows[chartRows.length - 1]?.[xAxis] ?? '')
    if (!first || !last) return 0
    return (new Date(last).getTime() - new Date(first).getTime()) / (1000 * 60 * 60 * 24)
}

export const tooltipLabelFormatter = (chartRows: GraphDataRow[], xAxis: string): TickFormatter | undefined => {
    const spanDays = calculateDuration(chartRows, xAxis)
    if (!spanDays || xAxis !== 'date') return undefined
    const bucketDays = chartRows.length > 1 ? Math.round(spanDays / (chartRows.length - 1)) : 1
    return (value) => {
        const d = new Date(String(value))
        if (isNaN(d.getTime())) return String(value)
        if (bucketDays <= 1) return formatDateTick(value)
        const end = new Date(d)
        end.setDate(end.getDate() + bucketDays - 1)
        return `${formatDateTick(d.toISOString().slice(0, 10))} – ${formatDateTick(end.toISOString().slice(0, 10))}`
    }
}

export const xAxisTickFormatter = (chartRows: GraphDataRow[], xAxis: string): TickFormatter | undefined => {
    const spanDays = calculateDuration(chartRows, xAxis)
    if (!spanDays || xAxis !== 'date') return undefined
    // Long duration: always show year on every tick
    if (spanDays > 365) {
        return (value: unknown) => {
            const d = new Date(String(value))
            if (isNaN(d.getTime())) return String(value)
            return `${d.toLocaleDateString('en-GB', { month: 'short' })} '${String(d.getFullYear()).slice(2)}`
        }
    // Medium duration: show year only in January
    } else if (spanDays > 90) {
        return (value: unknown) => {
            const d = new Date(String(value))
            if (isNaN(d.getTime())) return String(value)
            const month = d.toLocaleDateString('en-GB', { month: 'short' })
            return d.getMonth() === 0 ? `${month} '${String(d.getFullYear()).slice(2)}` : month
        }
    // Short duration: show day and month
    } else {
        return (value: unknown) => {
            const d = new Date(String(value))
            if (isNaN(d.getTime())) return String(value)
            return `${d.getDate()} ${d.toLocaleDateString('en-GB', { month: 'short' })}`
        }
    }
}

const GraphSpanDelimiters = ({ spans, dates, activeSpan }: GraphSpanDelimitersProps): ReactElement => (
    <>
        { spans.flatMap((span, i) => {
            const start = snapToNearest(span.date, dates)
            const end = snapToNearest(span.end, dates)
            const opacity = activeSpan && activeSpan !== span.title ? 0.15 : 1
            const strokeWidth = 4 - i
            return [
                <ReferenceLine
                    key={ `${span.title}-start` }
                    x={ start }
                    yAxisId={ 0 }
                    className="graph__span-line"
                    strokeDasharray="4 3"
                    strokeOpacity={ opacity }
                    strokeWidth={ strokeWidth } />,
                start !== end && <ReferenceLine
                    key={ `${span.title}-end` }
                    x={ end }
                    yAxisId={ 0 }
                    className="graph__span-line"
                    strokeDasharray="4 3"
                    strokeOpacity={ opacity }
                    strokeWidth={ strokeWidth } />
            ].filter(Boolean)
        }) }
    </>
)

const GraphLegend = ({ payload, spans, selectedKey, selectedSpan,
    onSeriesClick, onSeriesHover, onSpanClick, onSpanHover }: GraphLegendProps): ReactElement => (
    <div className="graph__legend">
        { (payload ?? []).some(({ value }) => value !== undefined) && (
            <div className="graph__legend-row">
                { (payload ?? []).map(({ value, label, color }) => value === undefined
                    ? null
                    : (
                        <span
                            key={ value }
                            className={ `graph__legend-item${selectedKey === value ? ' graph__legend-item--selected' : ''}` }
                            onMouseEnter={ () => onSeriesHover(value) }
                            onMouseLeave={ () => onSeriesHover(null) }
                            onClick={ onSeriesClick(value) }
                        >
                            <svg width="20" height="10">
                                <line x1="0" y1="5" x2="20" y2="5" stroke={ color } strokeWidth="2" />
                            </svg>
                            { label }
                        </span>
                    )) }
            </div>
        ) }
        { spans && spans.length > 0 && (
            <div className="graph__legend-row">
                { spans.map((span, i) => (
                    <span
                        key={ span.title }
                        className={ `graph__legend-item${selectedSpan === span.title ? ' graph__legend-item--selected' : ''}` }
                        onMouseEnter={ () => onSpanHover(span.title) }
                        onMouseLeave={ () => onSpanHover(null) }
                        onClick={ onSpanClick(span.title) }
                    >
                        <svg width="20" height="10">
                            <line x1="0" y1="5" x2="20" y2="5" className="graph__span-line" strokeWidth={ 4 - i } strokeDasharray="4 3" />
                        </svg>
                        { span.title }
                    </span>
                )) }
            </div>
        ) }
    </div>
)

// Outlier days grouped under a plotted x, keyed by that x, for the tooltip.
type OutlierGroup = { name: string, colour: string, days: OutlierDay[] }
type LineTooltipProps = {
    active?: boolean
    payload?: Array<{ name: string, value: number, color: string, payload?: GraphDataRow }>
    label?: string
    labelFormatter?: TickFormatter
    outlierDays?: Record<string, OutlierGroup[]>
    seriesColours?: Record<string, string>
}

// At most three significant figures, and never more than one decimal place (e.g. 22.876 -> 22.9).
export const formatTooltipValue = (value: number): string =>
    Number(value.toPrecision(3)).toLocaleString('en-GB', { maximumFractionDigits: 1 })

// e.g. '13 July: “Going somewhere” (62)' — day and month (year clear from context), title if any,
// and the day's own value (only used for bucketed points, whose line shows the bucket mean).
const outlierDayLabel = (day: OutlierDay): string => {
    const d = new Date(day.date)
    const date = isNaN(d.getTime()) ? day.date : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })
    const label = day.title ? `${date}: “${day.title}”` : date
    return `${label} (${formatTooltipValue(day.value)})`
}

export const LineTooltip = (
    { active, payload, label, labelFormatter, outlierDays, seriesColours }: LineTooltipProps
): ReactElement | null => {
    if (!active || !payload?.length) return null
    const dateLabel = label && labelFormatter ? labelFormatter(label) : label
    // Bucketed points carry an entry count and average over it; single days do not.
    const entries = payload[0]?.payload?.entries
    const bucketed = typeof entries === 'number'
    // The '__out' marker series carry only a plotting position; keep them out of the value list.
    const series = payload.filter(item => !item.name.endsWith('__out'))
    const groups = label != null ? outlierDays?.[String(label)] : undefined
    // On a single day the outlier IS this point: flag it inline and fold that day's entry title into
    // the header (every unbucketed row carries its title). A bucket lists its flagged days instead.
    const outlierNames = new Set((groups ?? []).map(group => group.name))
    const rowTitle = payload[0]?.payload?.title
    const dayTitle = !bucketed && typeof rowTitle === 'string' ? rowTitle : undefined
    return <div className="graph__tooltip">
        <p className="graph__tooltip-label">{ dayTitle ? `${dateLabel}: “${dayTitle}”` : dateLabel }</p>
        { typeof entries === 'number' && entries > 1
          && <p className="graph__tooltip-note">Average across { entries } entries:</p> }
        { series.map((item) => {
            const flagged = !bucketed && outlierNames.has(item.name)
            // A sparse metric's line has stroke "none", so recharts reports its colour as "none";
            // fall back to the passed series colour so the label isn't rendered black.
            const colour = seriesColours?.[item.name] ?? item.color
            return <p key={ item.name } className="graph__tooltip-item" style={ { color: colour } }>
                { item.name }: { formatTooltipValue(item.value) }
                { flagged && <span className="graph__tooltip-flag"> (<InlineStar /> unusually high)</span> }
            </p>
        }) }
        { bucketed && groups?.map(group => <div key={ group.name } className="graph__tooltip-outliers" style={ { color: group.colour } }>
            <p className="graph__tooltip-outlier-head">Unusually high { group.name.toLowerCase() }:</p>
            { group.days.map(day => <p key={ day.date } className="graph__tooltip-outlier-day">
                { outlierDayLabel(day) }
            </p>) }
        </div>) }
    </div>
}

// opacity lets a marker fade with its series when another is highlighted (Line opacity misses dots).
type OutlierDotProps = { cx?: number, cy?: number, value?: number | null, colour: string, opacity?: number }

// Path of a five-pointed star centred at (cx, cy).
const starPath = (cx: number, cy: number, outer: number, inner: number): string =>
    'M ' + Array.from({ length: 10 }, (_, i) => {
        const radius = i % 2 === 0 ? outer : inner
        const angle = -Math.PI / 2 + i * Math.PI / 5
        return `${(cx + radius * Math.cos(angle)).toFixed(2)},${(cy + radius * Math.sin(angle)).toFixed(2)}`
    }).join(' L ') + ' Z'

// Hollow star marking an outlier ("unusually high anything") — distinct from the filled star that
// denotes the rating metric elsewhere. Nothing at the series' non-outlier points.
const OutlierDot = ({ cx, cy, value, colour, opacity = 1 }: OutlierDotProps): ReactElement | null => {
    if (cx == null || cy == null || value == null) return null
    return <path d={ starPath(cx, cy, 5, 2.1) } fill="none" stroke={ colour } strokeWidth={ 1.4 } opacity={ opacity } />
}

// The same hollow star sized for inline use in the tooltip; inherits the surrounding text colour.
// The class out-specifies `.graph svg { width: 100% }`, which this would otherwise inherit.
const InlineStar = (): ReactElement => (
    <svg viewBox="0 0 12 12" className="graph__flag-star">
        <path d={ starPath(6, 6, 5, 2.1) } fill="none" stroke="currentColor" strokeWidth={ 1.2 } />
    </svg>
)

// Hollow lozenge (a tall card-suit diamond) marking each point of a sparse metric (too few for a line).
const SparseDot = ({ cx, cy, value, colour, opacity = 1 }: OutlierDotProps): ReactElement | null => {
    if (cx == null || cy == null || value == null) return null
    const rx = 3.3
    const ry = 5.5
    return <path
        d={ `M ${cx} ${cy - ry} L ${cx + rx} ${cy} L ${cx} ${cy + ry} L ${cx - rx} ${cy} Z` }
        fill="none"
        stroke={ colour }
        strokeWidth={ 1.5 }
        opacity={ opacity } />
}

// A metric with at most this many points in view is drawn as markers, not a connecting line.
const SPARSE_MAX_POINTS = 3

type YAxisLabelProps = {
    viewBox?: { x: number, y: number, width: number, height: number }
    value: string
    colour?: string
    side: 'left' | 'right'
}

// Rotated y-axis title, colour-matched to a single-series axis (neutral for a shared axis).
export const YAxisLabel = ({ viewBox, value, colour, side }: YAxisLabelProps): ReactElement | null => {
    if (!viewBox) return null
    const { x, y, width, height } = viewBox
    const cx = side === 'left' ? x + 14 : x + width - 14
    const cy = y + height / 2
    return <text
        x={ cx }
        y={ cy }
        transform={ `rotate(-90, ${cx}, ${cy})` }
        textAnchor="middle"
        style={ { fill: colour ?? 'var(--text)' } }>
        { value }
    </text>
}

// Colour for a series, by its position in the graph's series list.
const seriesColourFor = (seriesKeys: string[], key: string): string =>
    GRAPH_COLOURS[seriesKeys.indexOf(key) % GRAPH_COLOURS.length]

// Left+right y-axes for distinctly-scaled metrics (e.g. word count vs complexity); 3+ distinct
// scales fall back to one shared axis, since recharts practically supports only two.
const resolveAxisGroups = (data: GraphData, seriesKeys: string[]): GraphAxisGroup[] =>
    data.yAxisGroups && data.yAxisGroups.length >= 1 && data.yAxisGroups.length <= 2
        ? data.yAxisGroups
        : [{ title: data.yTitle ?? '', fields: seriesKeys }]

// Widen an axis for large tick values (e.g. word counts), so the rotated title clears the numbers.
const axisWidth = (fields: string[], chartRows: GraphDataRow[]): number => {
    const max = Math.max(0, ...chartRows.flatMap(r => fields.map(f => Math.abs(Number(r[f]) || 0))))
    return 60 + Math.max(0, Math.round(max).toString().length - 3) * 10
}

// Metrics with too few points for a meaningful line are drawn as markers instead of a line.
const findSparseMetrics = (seriesKeys: string[], chartRows: GraphDataRow[]): Set<string> =>
    new Set(seriesKeys.filter((key) => {
        const points = chartRows.filter(r => r[key] != null).length
        return points >= 1 && points <= SPARSE_MAX_POINTS
    }))

// Colour per display name, so the tooltip can colour a sparse (stroke-less) series' label too.
const buildSeriesColours = (
    seriesKeys: string[], fieldName: (key: string) => string, seriesColour: (key: string) => string
): Record<string, string> =>
    Object.fromEntries(seriesKeys.map(key => [fieldName(key), seriesColour(key)]))

const buildLegendPayload = (
    seriesKeys: string[], fieldName: (key: string) => string, seriesColour: (key: string) => string
): GraphLegendItem[] =>
    seriesKeys.map(key => ({ value: key, label: fieldName(key), color: seriesColour(key) }))

// The "star on the line" plumbing, quarantined. Recharts only draws dots at real data points, so to
// mark an outlier on the plotted line we add a stroke-less companion series ('<metric>__out') carrying
// that point's value. Returns the flagged-day lists per plotted x (for the tooltip), the rows to plot
// (augmented with the companion columns), and which metrics actually have markers.
type OutlierMarkers = {
    daysByX: Record<string, OutlierGroup[]>
    plottedRows: GraphDataRow[]
    outlierMetrics: string[]
}
const buildOutlierMarkers = (
    outliers: Record<string, OutlierMarker[]>,
    seriesKeys: string[],
    chartRows: GraphDataRow[],
    xAxis: string,
    fieldName: (key: string) => string,
    seriesColour: (key: string) => string
): OutlierMarkers => {
    const outlierMetrics = Object.keys(outliers).filter(m => seriesKeys.includes(m))
    const daysByX: Record<string, OutlierGroup[]> = {}
    const markerMetricsByX: Record<string, string[]> = {}
    for (const m of outlierMetrics) {
        for (const marker of outliers[m]) {
            if (!daysByX[marker.x]) daysByX[marker.x] = []
            daysByX[marker.x].push({ name: fieldName(m), colour: seriesColour(m), days: marker.days })
            if (!markerMetricsByX[marker.x]) markerMetricsByX[marker.x] = []
            markerMetricsByX[marker.x].push(m)
        }
    }
    const plottedRows = outlierMetrics.length === 0
        ? chartRows
        : chartRows.map((row) => {
            const metrics = markerMetricsByX[String(row[xAxis] ?? '')]
            if (!metrics) return row
            const augmented = { ...row }
            for (const m of metrics) augmented[`${m}__out`] = row[m]
            return augmented
        })
    return { daysByX, plottedRows, outlierMetrics }
}

const GraphLine = ({ data, onClick }: GraphProps): ReactElement => {
    const { xAxis = 'date', yAxes = [], title } = data
    // Data arrives from R with one row per date, one column per series.
    const chartRows = data.data
    const seriesKeys = yAxes
    const [hoveredKey, setHoveredKey] = useState<string | null>(null)
    const [selectedKey, setSelectedKey] = useState<string | null>(null)
    const [hoveredSpan, setHoveredSpan] = useState<string | null>(null)
    const [selectedSpan, setSelectedSpan] = useState<string | null>(null)
    const activeKey = selectedKey ?? hoveredKey
    const activeSpan = selectedSpan ?? hoveredSpan

    const seriesColour = (key: string) => seriesColourFor(seriesKeys, key)
    const fieldName = (key: string) => data.fieldNames?.[key] ?? key
    const axisGroups = resolveAxisGroups(data, seriesKeys)
    const axisIdOf = (key: string) => Math.max(0, axisGroups.findIndex(g => g.fields.includes(key)))

    const { daysByX, plottedRows, outlierMetrics }
        = buildOutlierMarkers(data.outliers ?? {}, seriesKeys, chartRows, xAxis, fieldName, seriesColour)
    const sparseMetrics = findSparseMetrics(seriesKeys, chartRows)
    const seriesColours = buildSeriesColours(seriesKeys, fieldName, seriesColour)

    const clearSelection = useCallback(() => {
        setSelectedKey(null)
        setSelectedSpan(null)
    }, [])
    const onSeriesClick = useCallback((value: string) => (e: React.MouseEvent) => {
        e.stopPropagation()
        setSelectedKey(prev => prev === value ? null : value)
        setSelectedSpan(null)
    }, [])
    const onSpanClick = useCallback((title: string) => (e: React.MouseEvent) => {
        e.stopPropagation()
        setSelectedSpan(prev => prev === title ? null : title)
        setSelectedKey(null)
    }, [])
    const showLegend = seriesKeys.length > 1 || (data.spans?.length ?? 0) > 0
    const legendPayload = buildLegendPayload(seriesKeys, fieldName, seriesColour)

    return (
        <div className="graph" onClick={ onClick }>
            { title && <h3 className="graph__title">{ title }</h3> }
            { showLegend && <GraphLegend
                payload={ legendPayload }
                spans={ data.spans }
                selectedKey={ selectedKey }
                selectedSpan={ selectedSpan }
                onSeriesClick={ onSeriesClick }
                onSeriesHover={ setHoveredKey }
                onSpanClick={ onSpanClick }
                onSpanHover={ setHoveredSpan }
            /> }
            <div className="graph__chart">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={ plottedRows as Record<string, unknown>[] } margin={ GRAPH_MARGIN } onClick={ clearSelection }>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey={ xAxis }
                            tickFormatter={ xAxisTickFormatter(chartRows, xAxis) }
                            interval={ xAxisInterval(chartRows) } />
                        { axisGroups.map((group, i) => {
                            const colour = group.fields.length === 1 ? seriesColour(group.fields[0]) : undefined
                            const side = i === 0 ? 'left' : 'right'
                            return <YAxis
                                key={ group.title || i }
                                yAxisId={ i }
                                orientation={ side }
                                width={ axisWidth(group.fields, chartRows) }
                                stroke={ colour }
                                tick={ colour ? { fill: colour } : undefined }
                                label={ <YAxisLabel value={ group.title } colour={ colour } side={ side } /> } />
                        }) }
                        <Tooltip
                            content={ <LineTooltip
                                labelFormatter={ tooltipLabelFormatter(chartRows, xAxis) }
                                outlierDays={ daysByX }
                                seriesColours={ seriesColours } /> }
                        />
                        { data.spans && data.spans.length > 0 && (
                            <GraphSpanDelimiters
                                spans={ data.spans }
                                dates={ chartRows.map(r => String(r[xAxis] ?? '')) }
                                activeSpan={ activeSpan }
                            />
                        ) }
                        { seriesKeys.map((key) => {
                            const sparse = sparseMetrics.has(key)
                            const dim = activeKey && activeKey !== key ? 0.15 : 1
                            return <Line
                                key={ key }
                                yAxisId={ axisIdOf(key) }
                                type="monotone"
                                dataKey={ key }
                                name={ fieldName(key) }
                                stroke={ sparse ? 'none' : seriesColour(key) }
                                dot={ sparse ? <SparseDot colour={ seriesColour(key) } opacity={ dim } /> : false }
                                connectNulls
                                opacity={ dim } />
                        }).reverse() }
                        { outlierMetrics.filter(m => !sparseMetrics.has(m)).map(m => (
                            <Line
                                key={ `${m}__out` }
                                yAxisId={ axisIdOf(m) }
                                dataKey={ `${m}__out` }
                                name={ `${m}__out` }
                                stroke="none"
                                legendType="none"
                                isAnimationActive={ false }
                                dot={ <OutlierDot colour={ seriesColour(m) } opacity={ activeKey && activeKey !== m ? 0.15 : 1 } /> }
                                activeDot={ false } />
                        )) }
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    )
}

export default GraphLine
